import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionDownstreamDatasourcesFilteredByTableQuery,
  VirtualConnectionDownstreamDatasourcesFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamDatasourcesConnection_nodes,
  VirtualConnectionDownstreamDatasourcesFilteredByTableQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamDatasourcesFilteredByTableQuery';
import {
  VirtualConnectionDownstreamDatasourcesQuery,
  VirtualConnectionDownstreamDatasourcesQuery_virtualConnections_downstreamDatasourcesConnection_nodes,
  VirtualConnectionDownstreamDatasourcesQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamDatasourcesQuery';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionDownstreamDatasourcesFilteredByTableQueryGql } from '../../queries/VirtualConnectionDownstreamDatasourcesFilteredByTableQueryGql';
import { VirtualConnectionDownstreamDatasourcesQueryGql } from '../../queries/VirtualConnectionDownstreamDatasourcesQueryGql';
import { DatasourcesDisplayGridRowData, getRowDataForDatasource, DatasourcesDisplayGrid } from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = VirtualConnectionDownstreamDatasourcesQueryVariables;
type QueryResults = VirtualConnectionDownstreamDatasourcesQuery;
type QueryResultListNode = VirtualConnectionDownstreamDatasourcesQuery_virtualConnections_downstreamDatasourcesConnection_nodes;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionDownstreamDatasourcesFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionDownstreamDatasourcesFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionDownstreamDatasourcesFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamDatasourcesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  return virtualConnection ? virtualConnection.downstreamDatasourcesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const table = virtualConnectionTables && virtualConnectionTables[0];
  return table ? table.downstreamDatasourcesConnection : null;
}

export type VirtualConnectionDownstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function VirtualConnectionDownstreamDatasourcesQueryGrid(props: VirtualConnectionDownstreamDatasourcesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDatasourcesLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamDatasourcesFilteredByTableQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as PublishedDatasourceSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatasource}
    >
      {({ rowStore }) => <DatasourcesDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={VirtualConnectionDownstreamDatasourcesQueryGql}
        partialQueryVariables={{
          vizportalUrlId: props.vizportalUrlId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
  );
}
